
/**
 * Utility for monitoring app performance
 */

export const initPerformanceMonitoring = () => {
  // Skip in development mode to avoid noise
  if (import.meta.env.DEV) {
    return;
  }
  
  // Mark navigation timing
  markNavigationTiming();
  
  // Set up observer for paint timing
  observePaintTiming();
  
  // Set up observer for layout shifts
  observeLayoutShifts();
  
  // Report after page load completes
  window.addEventListener('load', () => {
    // Wait a bit to capture post-load metrics
    setTimeout(reportPerformance, 3000);
  });
};

const markNavigationTiming = () => {
  try {
    const navEntry = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
    if (navEntry) {
      console.log('Navigation Timing:', {
        dns: navEntry.domainLookupEnd - navEntry.domainLookupStart,
        tcp: navEntry.connectEnd - navEntry.connectStart,
        ttfb: navEntry.responseStart - navEntry.requestStart,
        responseTime: navEntry.responseEnd - navEntry.responseStart,
        domInteractive: navEntry.domInteractive - navEntry.fetchStart,
        domComplete: navEntry.domComplete - navEntry.fetchStart,
        loadEvent: navEntry.loadEventEnd - navEntry.fetchStart,
      });
    }
  } catch (e) {
    console.error('Error measuring navigation timing', e);
  }
};

const observePaintTiming = () => {
  try {
    const observer = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        console.log(`[Paint] ${entry.name}: ${entry.startTime.toFixed(0)}ms`);
      }
    });
    
    observer.observe({ entryTypes: ['paint', 'largest-contentful-paint'] });
  } catch (e) {
    console.error('Error observing paint timing', e);
  }
};

const observeLayoutShifts = () => {
  try {
    let clsValue = 0;
    
    const observer = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        // Use type assertion to access layout shift specific properties
        const layoutShift = entry as PerformanceEntry & { 
          hadRecentInput?: boolean;
          value?: number;
        };
        
        if (!layoutShift.hadRecentInput) {
          clsValue += layoutShift.value || 0;
        }
      }
      console.log(`Cumulative Layout Shift: ${clsValue.toFixed(3)}`);
    });
    
    observer.observe({ type: 'layout-shift', buffered: true });
  } catch (e) {
    console.error('Error observing layout shifts', e);
  }
};

const reportPerformance = () => {
  console.log('=== Performance Report ===');
  
  // Report resource timing
  reportResourceTiming();
  
  // Custom metrics
  reportCustomMetrics();
};

const reportResourceTiming = () => {
  try {
    const resources = performance.getEntriesByType('resource');
    const totalResources = resources.length;
    const totalSize = resources.reduce((acc, resource) => acc + (resource as any).encodedBodySize || 0, 0);
    
    console.log('Resource Timing:', {
      count: totalResources,
      totalSize: `${(totalSize / 1024 / 1024).toFixed(2)} MB`,
      js: resources.filter(r => r.name.endsWith('.js')).length,
      css: resources.filter(r => r.name.endsWith('.css')).length,
      images: resources.filter(r => 
        r.name.endsWith('.jpg') || 
        r.name.endsWith('.jpeg') || 
        r.name.endsWith('.png') || 
        r.name.endsWith('.webp')
      ).length,
    });
  } catch (e) {
    console.error('Error reporting resource timing', e);
  }
};

const reportCustomMetrics = () => {
  try {
    const customMeasures = performance.getEntriesByType('measure');
    
    for (const measure of customMeasures) {
      console.log(`[Custom] ${measure.name}: ${measure.duration.toFixed(2)}ms`);
    }
  } catch (e) {
    console.error('Error reporting custom metrics', e);
  }
};

// Mark a performance stage with automatic cleanup
export const markPerformanceStage = (stageName: string) => {
  const startMark = `${stageName}-start`;
  const endMark = `${stageName}-end`;
  const measureName = `${stageName}-duration`;
  
  // Start timing
  performance.mark(startMark);
  
  // Return a function to end timing
  return () => {
    performance.mark(endMark);
    performance.measure(measureName, startMark, endMark);
    
    // Log the measurement
    const measurements = performance.getEntriesByName(measureName);
    if (measurements.length > 0) {
      console.log(`[Stage] ${stageName}: ${measurements[0].duration.toFixed(2)}ms`);
    }
    
    // Clean up
    performance.clearMarks(startMark);
    performance.clearMarks(endMark);
    performance.clearMeasures(measureName);
  };
};
